import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ChevronDown, Lock, Pencil, Plus, Trash2, Upload, X } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { parseCsv, TEMPLATE } from "./OnboardingChecklist";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { companyOrSharedDemo } from "@/lib/demo";

type Customer = {
  id: string;
  name: string;
  address: string;
  city: string | null;
  zip: string | null;
  price: number | null;
  frequency: string | null;
  service_day: string | null;
  pay_method: string | null;
  source: string | null;
  phone: string | null;
  email: string | null;
  notes: string | null;
  missed_status?: string | null;
  is_shared_demo?: boolean | null;
  company_id?: string | null;
};

type MissedJob = {
  id: string;
  customer_id: string | null;
  customer_name: string;
  address: string;
  scheduled_date: string;
  crew: string | null;
  customers: Customer | null;
};

type Props = {
  companyId: string;
  isOwner: boolean;
  refreshKey?: number;
  onCustomersChanged?: () => void;
  /** When provided, the source tabs are controlled from the sidebar instead of shown in-card. */
  source?: "legacy" | "mowzi" | "missed";
};

const SERVICE_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

/** How a company is handling a customer whose service was missed. */
const MISSED_STATUSES = [
  { id: "catchup_scheduled", label: "Catch-up scheduled", tone: "bg-primary/15 text-primary" },
  { id: "needs_rescheduling", label: "Needs rescheduling", tone: "bg-destructive/15 text-destructive" },
  { id: "paused", label: "Paused / skipped", tone: "bg-muted text-muted-foreground" },
  { id: "removed", label: "Removed", tone: "bg-muted text-muted-foreground line-through" },
] as const;

const missedStatusMeta = (id: string | null | undefined) =>
  MISSED_STATUSES.find((s) => s.id === id) ?? MISSED_STATUSES[1];

const ONE_TIME_VALUES = new Set(["one-time", "one time", "onetime", "once", "single"]);
const isOneTime = (frequency: string | null) =>
  ONE_TIME_VALUES.has((frequency ?? "").trim().toLowerCase());


const emptyDraft: {
  name: string;
  address: string;
  city: string;
  zip: string;
  price: string;
  frequency: string;
  service_day: string;
  pay_method: string;
  source: string;
  phone: string;
  email: string;
} = {
  name: "",
  address: "",
  city: "",
  zip: "",
  price: "",
  frequency: "weekly",
  service_day: "",
  pay_method: "other",
  source: "legacy",
  phone: "",
  email: "",
};

export default function CustomerList({
  companyId,
  isOwner,
  refreshKey = 0,
  onCustomersChanged,
  source,
}: Props) {
  const [rows, setRows] = useState<Customer[]>([]);
  const [missed, setMissed] = useState<MissedJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [draft, setDraft] = useState<(typeof emptyDraft & { id?: string }) | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [openRecurring, setOpenRecurring] = useState(true);
  const [openOneTime, setOpenOneTime] = useState(false);
  const [openMissed, setOpenMissed] = useState(true);
  const [internalTab, setInternalTab] = useState<"legacy" | "mowzi">("legacy");
  const view = source ?? internalTab;
  const missedOnly = view === "missed";
  const sourceTab = view === "mowzi" ? "mowzi" : "legacy";
  const setSourceTab = setInternalTab;
  const [catchupWeeks, setCatchupWeeks] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const fileRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    setLoading(true);
    const today = new Date().toISOString().slice(0, 10);
    const [{ data, error }, missedRes, companyRes] = await Promise.all([
      supabase
        .from("customers")
        .select("id, name, address, city, zip, price, frequency, service_day, pay_method, source, phone, email, notes, missed_status, is_shared_demo, company_id")
        .or(companyOrSharedDemo(companyId))
        .order("name", { ascending: true }),
      supabase
        .from("jobs")
        .select(
          "id, customer_id, customer_name, address, scheduled_date, crew, status, customers!left(id, name, address, city, zip, price, frequency, service_day, pay_method, source, phone, email, notes, missed_status)"
        )
        .eq("company_id", companyId)
        .lt("scheduled_date", today)
        .in("status", ["scheduled", "in_progress"])
        .order("scheduled_date", { ascending: false })
        .limit(200),
      supabase.from("companies").select("missed_catchup_weeks").eq("id", companyId).maybeSingle(),
    ]);
    if (error) {
      toast({ title: "Could not load customers", description: error.message, variant: "destructive" });
    }
    setRows((data as Customer[]) ?? []);
    setMissed(((missedRes.data as MissedJob[]) ?? []));
    setCatchupWeeks(companyRes.data?.missed_catchup_weeks ?? 1);
    setLoading(false);
  };

  /** Owner sets how a missed customer should be handled. */
  const setMissedStatus = async (customerId: string, status: string) => {
    setMissed((prev) =>
      prev.map((m) =>
        m.customer_id === customerId && m.customers
          ? { ...m, customers: { ...m.customers, missed_status: status } }
          : m,
      ),
    );
    const { error } = await supabase
      .from("customers")
      .update({ missed_status: status })
      .eq("id", customerId);
    if (error) {
      toast({ title: "Could not update status", description: error.message, variant: "destructive" });
      load();
    }
  };

  /** Company-wide default: how long we keep auto-trying a catch-up. */
  const saveCatchupWeeks = async (weeks: number) => {
    setCatchupWeeks(weeks);
    const { error } = await supabase
      .from("companies")
      .update({ missed_catchup_weeks: weeks })
      .eq("id", companyId);
    if (error) {
      toast({ title: "Could not save setting", description: error.message, variant: "destructive" });
    }
  };


  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, refreshKey]);

  const isMowzi = (s: string | null | undefined) => s === "Mowzi";

  const sourceRows = useMemo(
    () => rows.filter((r) => (sourceTab === "mowzi" ? isMowzi(r.source) : !isMowzi(r.source))),
    [rows, sourceTab],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sourceRows;
    return sourceRows.filter((r) =>
      [r.name, r.address, r.city, r.zip, r.phone, r.email, r.service_day, r.pay_method, r.source]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [sourceRows, query]);

  const recurring = useMemo(() => filtered.filter((c) => !isOneTime(c.frequency)), [filtered]);
  const oneTime = useMemo(() => filtered.filter((c) => isOneTime(c.frequency)), [filtered]);
  const groupedMissed = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const map = new Map<string, MissedJob & { weeksSince: number }>();
    for (const m of missed) {
      if (isMowzi(m.customers?.source)) continue;
      const key = m.customer_id ?? m.address.trim().toLowerCase();
      const existing = map.get(key);
      const days = Math.floor(
        (today.getTime() - new Date(`${m.scheduled_date}T00:00:00`).getTime()) / (1000 * 60 * 60 * 24),
      );
      const weeksSince = Math.max(0, Math.floor(days / 7));
      if (!existing) {
        map.set(key, { ...m, weeksSince });
      } else if (m.scheduled_date > existing.scheduled_date) {
        existing.scheduled_date = m.scheduled_date;
        existing.crew = m.crew;
        existing.customers = m.customers;
        existing.weeksSince = weeksSince;
      }
    }
    return [...map.values()].sort(
      (a, b) => b.weeksSince - a.weeksSince || b.scheduled_date.localeCompare(a.scheduled_date),
    );
  }, [missed]);

  const filteredMissed = useMemo(() => {
    const q = query.trim().toLowerCase();
    let bySource = groupedMissed.filter((m) =>
      sourceTab === "mowzi" ? isMowzi(m.customers?.source) : !isMowzi(m.customers?.source),
    );
    bySource = bySource.filter((m) => {
      const status = m.customers?.missed_status ?? "needs_rescheduling";
      if (statusFilter === "all") return status !== "removed";
      return status === statusFilter;
    });
    if (!q) return bySource;
    return bySource.filter((m) => {
      const c = m.customers;
      const fields = c
        ? [c.name, c.address, c.city, c.zip, c.phone, c.email, c.service_day, c.pay_method, c.source, m.customer_name, m.address]
        : [m.customer_name, m.address];
      return fields.filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
    });
  }, [groupedMissed, query, sourceTab, statusFilter]);



  const remove = async (id: string) => {
    const { error } = await supabase.from("customers").delete().eq("id", id);
    if (error) {
      toast({ title: "Could not remove customer", description: error.message, variant: "destructive" });
      return;
    }
    setRows((prev) => prev.filter((r) => r.id !== id));
    onCustomersChanged?.();
    toast({ title: "Customer removed" });
  };

  /** Shared demo customers belong to the test pool and are read-only here. */
  const isSharedDemo = (c: Customer) => !!c.is_shared_demo && c.company_id !== companyId;

  const startEdit = (c: Customer) => {
    if (isSharedDemo(c)) {
      toast({
        title: "Shared demo customer",
        description: "Sample customers are shared across every demo workspace and can't be edited.",
      });
      return;
    }
    if (c.source === "Mowzi") {
      toast({
        title: "Mowzi customer",
        description: "Mowzi customers are managed by Mowzi and can't be edited here.",
      });
      return;
    }
    setDraft({
      id: c.id,
      name: c.name,
      address: c.address,
      city: c.city ?? "",
      zip: c.zip ?? "",
      price: c.price != null ? String(c.price) : "",
      frequency: c.frequency ?? "weekly",
      service_day: c.service_day ?? "",
      pay_method: c.pay_method ?? "other",
      source: c.source ?? "legacy",
      phone: c.phone ?? "",
      email: c.email ?? "",
    });
  };

  const startDraft = (frequency: "weekly" | "one-time") => {
    setDraft({ ...emptyDraft, frequency });
    if (frequency === "one-time") {
      setOpenOneTime(true);
    } else {
      setOpenRecurring(true);
    }
  };

  const saveDraft = async () => {
    if (!draft) return;
    if (!draft.name.trim() || !draft.address.trim()) {
      toast({ title: "Name and address are required", variant: "destructive" });
      return;
    }
    const priceNum = Number(draft.price.replace(/[^0-9.]/g, ""));
    const payload = {
      name: draft.name.trim(),
      address: draft.address.trim(),
      city: draft.city.trim() || null,
      zip: draft.zip.trim() || null,
      price: Number.isFinite(priceNum) && priceNum > 0 ? priceNum : null,
      frequency: draft.frequency.trim() || "weekly",
      service_day: draft.service_day || null,
      pay_method: draft.pay_method,
      source: draft.source || "legacy",
      phone: draft.phone.trim() || null,
      email: draft.email.trim() || null,
    };
    const cols =
      "id, name, address, city, zip, price, frequency, service_day, pay_method, source, phone, email, notes";
    setSaving(true);
    const { data, error } = draft.id
      ? await supabase.from("customers").update(payload).eq("id", draft.id).select(cols).single()
      : await supabase
          .from("customers")
          .insert({ company_id: companyId, ...payload })
          .select(cols)
          .single();
    setSaving(false);
    if (error) {
      toast({ title: "Could not save customer", description: error.message, variant: "destructive" });
      return;
    }
    const saved = data as Customer;
    setRows((prev) =>
      (draft.id ? prev.map((r) => (r.id === saved.id ? saved : r)) : [...prev, saved]).sort((a, b) =>
        a.name.localeCompare(b.name),
      ),
    );
    if (isOneTime(saved.frequency)) setOpenOneTime(true);
    else setOpenRecurring(true);
    setDraft(null);
    onCustomersChanged?.();
    toast({ title: draft.id ? "Customer updated" : "Customer added" });
  };

  const handleFile = async (file: File) => {
    setUploading(true);
    try {
      const text = await file.text();
      const parsed = parseCsv(text);
      if (parsed.fatal || parsed.rows.length === 0) {
        toast({
          title: "Nothing imported",
          description: parsed.fatal ?? "No valid rows found in that file.",
          variant: "destructive",
        });
        return;
      }
      const existing = new Set(rows.map((r) => r.address.trim().toLowerCase()));
      const seen = new Set<string>();
      const toInsert = parsed.rows
        .map((r) => r.data)
        .filter((d) => {
          const key = d.address.trim().toLowerCase();
          if (existing.has(key) || seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      const skipped = parsed.rows.length - toInsert.length + parsed.issues.length;
      if (toInsert.length === 0) {
        toast({ title: "No new customers", description: `${skipped} row(s) skipped as duplicates or invalid.` });
        return;
      }
      const { data, error } = await supabase
        .from("customers")
        .insert(toInsert.map((d) => ({ company_id: companyId, ...d })))
        .select(
          "id, name, address, city, zip, price, frequency, service_day, pay_method, source, phone, email, notes",
        );
      if (error) {
        toast({ title: "Import failed", description: error.message, variant: "destructive" });
        return;
      }
      setRows((prev) =>
        [...prev, ...((data as Customer[]) ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
      );
      onCustomersChanged?.();
      toast({
        title: `Imported ${data?.length ?? 0} customer(s)`,
        description: skipped > 0 ? `${skipped} row(s) skipped.` : undefined,
      });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const downloadTemplate = () => {
    const url = URL.createObjectURL(new Blob([TEMPLATE], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "customer-list-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const cellInput = "h-6 w-full rounded-sm border px-1 text-xs";

  const editorRow = draft ? (
    <tr className="border-t border-border bg-accent/40 [&>td]:border-r [&>td]:border-border [&>td]:px-1 [&>td]:py-0.5 [&>td:last-child]:border-r-0">
      <td>
        <input
          autoFocus
          className={cellInput}
          placeholder="Jane Doe"
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
        />
      </td>
      <td>
        <input
          className={cellInput}
          placeholder="123 Oak St"
          value={draft.address}
          onChange={(e) => setDraft({ ...draft, address: e.target.value })}
        />
      </td>
      <td>
        <input
          className={cellInput}
          placeholder="Denver"
          value={draft.city}
          onChange={(e) => setDraft({ ...draft, city: e.target.value })}
        />
      </td>
      <td>
        <input
          className={cellInput}
          placeholder="80202"
          value={draft.zip}
          onChange={(e) => setDraft({ ...draft, zip: e.target.value })}
        />
      </td>
      <td>
        <input
          className={`${cellInput} text-right`}
          placeholder="45"
          value={draft.price}
          onChange={(e) => setDraft({ ...draft, price: e.target.value })}
        />
      </td>
      <td>
        <select
          className={cellInput}
          value={draft.frequency}
          onChange={(e) => setDraft({ ...draft, frequency: e.target.value })}
        >
          <option value="weekly">weekly</option>
          <option value="biweekly">biweekly</option>
          <option value="monthly">monthly</option>
          <option value="one-time">one-time</option>
        </select>
      </td>
      <td>
        <select
          className={cellInput}
          value={draft.service_day}
          onChange={(e) => setDraft({ ...draft, service_day: e.target.value })}
        >
          <option value="">Not set</option>
          {SERVICE_DAYS.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
      </td>
      <td>
        <select
          className={cellInput}
          value={draft.pay_method}
          onChange={(e) => setDraft({ ...draft, pay_method: e.target.value })}
        >
          <option value="Mowzi">Mowzi</option>
          <option value="other">Other</option>
        </select>
      </td>
      <td>
        <select
          className={cellInput}
          value={draft.source}
          onChange={(e) => setDraft({ ...draft, source: e.target.value })}
        >
          <option value="legacy">Legacy</option>
          <option value="Mowzi">Mowzi</option>
        </select>
      </td>
      <td>
        <input
          className={cellInput}
          placeholder="Phone"
          value={draft.phone}
          onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
          onKeyDown={(e) => e.key === "Enter" && saveDraft()}
        />
      </td>
      <td>
        <input
          className={cellInput}
          placeholder="Email"
          value={draft.email}
          onChange={(e) => setDraft({ ...draft, email: e.target.value })}
          onKeyDown={(e) => e.key === "Enter" && saveDraft()}
        />
      </td>
      {isOwner && (
        <td className="whitespace-nowrap text-center">
          <div className="flex items-center gap-1">
            <Button size="sm" className="h-6 px-2 text-[11px]" disabled={saving} onClick={saveDraft}>
              Save
            </Button>
            <button
              type="button"
              aria-label="Cancel edit"
              onClick={() => setDraft(null)}
              className="text-muted-foreground hover:text-destructive"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        </td>
      )}
    </tr>
  ) : null;

  const renderRow = (c: Customer) => (
    <tr
      key={c.id}
      className="border-t border-border even:bg-muted/40 hover:bg-accent/50 [&>td]:max-w-[16rem] [&>td]:truncate [&>td]:border-r [&>td]:border-border [&>td]:px-2 [&>td]:py-0.5 [&>td:last-child]:border-r-0"
    >
      <td className="font-medium">{c.name}</td>
      <td>{c.address}</td>
      <td>{c.city ?? "—"}</td>
      <td>{c.zip ?? "—"}</td>
      <td className="text-right">{c.price != null ? `$${Number(c.price).toFixed(2)}` : "—"}</td>
      <td className="text-muted-foreground">{c.frequency ?? "weekly"}</td>
      <td className="text-muted-foreground">{c.service_day ?? "—"}</td>
      <td className={c.pay_method === "mowzi" ? "font-medium text-primary" : "text-muted-foreground"}>
        {c.pay_method === "mowzi" ? "Mowzi" : "Other"}
      </td>
      <td>
        <span
          className={`inline-block rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
            c.source === "Mowzi" ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"
          }`}
        >
          {c.source === "Mowzi" ? "Mowzi" : "Legacy"}
        </span>
      </td>
      <td className="text-muted-foreground">
        {c.phone ? (
          <a className="hover:text-primary hover:underline" href={`tel:${c.phone.replace(/[^\d+]/g, "")}`}>
            {c.phone}
          </a>
        ) : (
          "—"
        )}
      </td>
      <td className="text-muted-foreground">
        {c.email ? (
          <a className="hover:text-primary hover:underline" href={`mailto:${c.email}`}>
            {c.email}
          </a>
        ) : (
          "—"
        )}
      </td>
      {isOwner && (
        <td className="whitespace-nowrap text-center">
          {c.source === "Mowzi" || isSharedDemo(c) ? (
            <span
              className="inline-flex items-center justify-center text-muted-foreground"
              title={isSharedDemo(c) ? "Shared demo customer — not editable" : "Mowzi customer — not editable"}
            >
              <Lock className="h-3 w-3" />
            </span>
          ) : (
            <div className="flex items-center justify-center gap-2">
              <button
                type="button"
                aria-label={`Edit ${c.name}`}
                onClick={() => startEdit(c)}
                className="text-muted-foreground hover:text-primary"
              >
                <Pencil className="h-3 w-3" />
              </button>
              <button
                type="button"
                aria-label={`Remove ${c.name}`}
                onClick={() => remove(c.id)}
                className="text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          )}
        </td>
      )}
    </tr>
  );

  const renderTable = (list: Customer[], showNewDraft: boolean, emptyText: string) => {
    if (list.length === 0 && !showNewDraft) {
      return <p className="py-4 text-center text-xs text-muted-foreground">{emptyText}</p>;
    }
    return (
      <div className="max-h-[26rem] overflow-auto rounded border">
        <table className="w-full border-collapse text-xs tabular-nums">
          <thead className="sticky top-0 z-10 bg-muted">
            <tr className="[&>th]:whitespace-nowrap [&>th]:border-r [&>th]:border-border [&>th]:px-2 [&>th]:py-1 [&>th]:text-left [&>th]:font-semibold [&>th:last-child]:border-r-0">
              <th>Name</th>
              <th>Address</th>
              <th>City</th>
              <th>Zip</th>
              <th className="text-right">Price</th>
              <th>Frequency</th>
              <th>Day of service</th>
              <th>Pay</th>
              <th>Source</th>
              <th>Phone</th>
              <th>Email</th>
              {isOwner && <th className="w-8" />}
            </tr>
          </thead>
          <tbody>
            {showNewDraft && editorRow}
            {list.map((c) =>
              draft?.id === c.id ? <Fragment key={c.id}>{editorRow}</Fragment> : renderRow(c),
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const renderMissedRow = (m: MissedJob & { weeksSince: number }) => {
    const c = m.customers;
    const baseClass =
      "border-t border-border even:bg-muted/40 hover:bg-accent/50 [&>td]:max-w-[16rem] [&>td]:truncate [&>td]:border-r [&>td]:border-border [&>td]:px-2 [&>td]:py-0.5 [&>td:last-child]:border-r-0";
    const cells = c
      ? [
          <td key="n" className="font-medium">{c.name}</td>,
          <td key="a">{c.address}</td>,
          <td key="c">{c.city ?? "—"}</td>,
          <td key="z">{c.zip ?? "—"}</td>,
          <td key="p" className="text-right">{c.price != null ? `$${Number(c.price).toFixed(2)}` : "—"}</td>,
          <td key="f" className="text-muted-foreground">{c.frequency ?? "weekly"}</td>,
          <td key="d" className="text-muted-foreground">{c.service_day ?? "—"}</td>,
          <td key="pay" className={c.pay_method === "mowzi" ? "font-medium text-primary" : "text-muted-foreground"}>
            {c.pay_method === "mowzi" ? "Mowzi" : "Other"}
          </td>,
          <td key="s">
            <span
              className={`inline-block rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                c.source === "Mowzi" ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"
              }`}
            >
              {c.source === "Mowzi" ? "Mowzi" : "Legacy"}
            </span>
          </td>,
          <td key="ph" className="text-muted-foreground">{c.phone ?? "—"}</td>,
          <td key="em" className="text-muted-foreground">{c.email ?? "—"}</td>,
        ]
      : [
          <td key="n" className="font-medium">{m.customer_name}</td>,
          <td key="a">{m.address}</td>,
          <td key="c">—</td>,
          <td key="z">—</td>,
          <td key="p" className="text-right">—</td>,
          <td key="f" className="text-muted-foreground">—</td>,
          <td key="d" className="text-muted-foreground">—</td>,
          <td key="pay" className="text-muted-foreground">—</td>,
          <td key="s">—</td>,
          <td key="ph" className="text-muted-foreground">—</td>,
          <td key="em" className="text-muted-foreground">—</td>,
        ];
    const status = c?.missed_status ?? "needs_rescheduling";
    const meta = missedStatusMeta(status);
    const autoCatchup = status === "needs_rescheduling" && m.weeksSince <= catchupWeeks;
    return (
      <tr key={m.id} className={baseClass}>
        {cells}
        <td key="lm" className="text-destructive">{m.scheduled_date}</td>
        <td key="ws" className="text-right">
          <span
            className={`inline-block rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
              m.weeksSince > 2 ? "bg-destructive/15 text-destructive" : "bg-muted text-muted-foreground"
            }`}
          >
            {m.weeksSince}w
          </span>
        </td>
        <td key="ms" className="!max-w-none">
          {isOwner && c ? (
            <select
              value={status}
              onChange={(e) => setMissedStatus(c.id, e.target.value)}
              className="h-6 w-full rounded border bg-background px-1 text-[11px]"
            >
              {MISSED_STATUSES.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          ) : (
            <span className={`inline-block rounded-full px-1.5 py-0.5 text-[10px] font-medium ${meta.tone}`}>
              {meta.label}
            </span>
          )}
          {autoCatchup && (
            <span className="ml-1 text-[10px] text-muted-foreground">auto catch-up</span>
          )}
        </td>
      </tr>
    );
  };

  const renderMissedTable = (list: (MissedJob & { weeksSince: number })[], emptyText: string) => {
    if (list.length === 0) {
      return <p className="py-4 text-center text-xs text-muted-foreground">{emptyText}</p>;
    }
    return (
      <div className="max-h-[26rem] overflow-auto rounded border">
        <table className="w-full border-collapse text-xs tabular-nums">
          <thead className="sticky top-0 z-10 bg-muted">
            <tr className="[&>th]:whitespace-nowrap [&>th]:border-r [&>th]:border-border [&>th]:px-2 [&>th]:py-1 [&>th]:text-left [&>th]:font-semibold [&>th:last-child]:border-r-0">
              <th>Name</th>
              <th>Address</th>
              <th>City</th>
              <th>Zip</th>
              <th className="text-right">Price</th>
              <th>Frequency</th>
              <th>Day of service</th>
              <th>Pay</th>
              <th>Source</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Last missed</th>
              <th className="text-right">Weeks since</th>
              <th>Handling</th>
            </tr>

          </thead>
          <tbody>{list.map((m) => renderMissedRow(m))}</tbody>
        </table>
      </div>
    );
  };

  const section = (
    title: string,
    count: number,
    open: boolean,
    setOpen: (v: boolean) => void,
    body: React.ReactNode,
    subtitle?: string,
    addButton?: React.ReactNode,
  ) => (
    <Collapsible open={open} onOpenChange={setOpen} className="rounded-md border">
      <CollapsibleTrigger className="flex w-full flex-wrap items-center justify-between gap-2 px-3 py-2 text-left">
        <div className="flex items-center gap-2">
          <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${open ? "" : "-rotate-90"}`} />
          <span className="text-sm font-medium">{title}</span>
          <span className="text-xs text-muted-foreground">({count})</span>
          {subtitle && <span className="text-[11px] text-muted-foreground">{subtitle}</span>}
        </div>
        {addButton && <div className="flex items-center">{addButton}</div>}
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="px-3 pb-3">{body}</div>
      </CollapsibleContent>
    </Collapsible>
  );

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0 py-3">
        <div>
          <CardTitle className="text-sm">
            {missedOnly ? "Missed service" : sourceTab === "mowzi" ? "Mowzi customers" : "My customers"}{" "}
            <span className="text-xs font-normal text-muted-foreground">
              ({missedOnly ? filteredMissed.length : sourceRows.length})
            </span>
          </CardTitle>
          <p className="text-[11px] text-muted-foreground">
            {missedOnly
              ? "Past jobs that were never completed."
              : sourceTab === "mowzi"
                ? "Sent to you by Mowzi — managed by Mowzi."
                : "Customers you added or imported."}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, address, phone…"
            className="h-7 w-full text-xs sm:w-56"
          />
          {missedOnly && (
            <>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="h-7 rounded border bg-background px-2 text-xs"
                aria-label="Filter by handling"
              >
                <option value="all">All open</option>
                {MISSED_STATUSES.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
              {isOwner && (
                <label className="flex items-center gap-1 text-[11px] text-muted-foreground">
                  Auto catch-up for
                  <select
                    value={catchupWeeks}
                    onChange={(e) => saveCatchupWeeks(Number(e.target.value))}
                    className="h-7 rounded border bg-background px-1 text-xs"
                  >
                    {[0, 1, 2, 3, 4].map((w) => (
                      <option key={w} value={w}>
                        {w === 0 ? "off" : `${w} wk${w > 1 ? "s" : ""}`}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </>
          )}

          {isOwner && !missedOnly && sourceTab === "legacy" && (
            <>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFile(f);
                }}
              />
              <Button
                size="sm"
                className="h-7 shrink-0 text-xs"
                onClick={() => {
                  if (openOneTime && !openRecurring) {
                    startDraft("one-time");
                  } else {
                    setOpenRecurring(true);
                    startDraft("weekly");
                  }
                }}
              >
                <Plus className="mr-1 h-3 w-3" />
                Add customer
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 shrink-0 text-xs"
                disabled={uploading}
                onClick={() => fileRef.current?.click()}
              >
                <Upload className="mr-1 h-3 w-3" />
                {uploading ? "Importing…" : "Bulk upload"}
              </Button>
              <Button size="sm" variant="ghost" className="h-7 shrink-0 text-xs" onClick={downloadTemplate}>
                Template
              </Button>
            </>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-2 pb-3">
        {!source && (
          <div className="flex flex-wrap items-center gap-1 rounded-md bg-muted p-1">
            {([
              { id: "legacy" as const, label: "My customers", hint: "added or imported by you" },
              { id: "mowzi" as const, label: "Mowzi customers", hint: "sent to you by Mowzi" },
            ]).map((t) => {
              const count = rows.filter((r) =>
                t.id === "mowzi" ? isMowzi(r.source) : !isMowzi(r.source),
              ).length;
              const active = sourceTab === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setSourceTab(t.id)}
                  className={`flex-1 rounded px-3 py-1.5 text-left text-xs transition-colors ${
                    active
                      ? "bg-background font-medium text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <span>{t.label}</span>{" "}
                  <span className="text-[11px] text-muted-foreground">({count})</span>
                  <span className="block text-[10px] text-muted-foreground">{t.hint}</span>
                </button>
              );
            })}
          </div>
        )}
        {loading ? (
          <p className="py-4 text-center text-xs text-muted-foreground">Loading customers…</p>
        ) : missedOnly ? (
          section(
            "Missed service",
            filteredMissed.length,
            openMissed,
            setOpenMissed,
            renderMissedTable(
              filteredMissed,
              "No missed services — every past job was completed or canceled.",
            ),
            "past jobs never completed",
          )
        ) : (
          <>
            {section(
              "Recurring customers",
              recurring.length,
              openRecurring,
              setOpenRecurring,
              renderTable(
                recurring,
                !!draft && !draft.id && !isOneTime(draft.frequency) && sourceTab === "legacy",
                sourceTab === "mowzi"
                  ? "No Mowzi customers yet — these appear when Mowzi sends you new work."
                  : sourceRows.length === 0
                    ? "No customers yet — import your list above or add one below."
                    : "No recurring customers match your search.",
              ),
              "weekly / biweekly / monthly",
              isOwner && sourceTab === "legacy" ? (
                <Button
                  size="sm"
                  variant="ghost"
                  className="ml-auto h-6 text-[11px]"
                  onClick={(e) => {
                    e.stopPropagation();
                    startDraft("weekly");
                  }}
                >
                  <Plus className="mr-1 h-3 w-3" />
                  Add recurring
                </Button>
              ) : undefined,
            )}
            {section(
              "One-time customers",
              oneTime.length,
              openOneTime,
              setOpenOneTime,
              renderTable(
                oneTime,
                !!draft && !draft.id && isOneTime(draft.frequency) && sourceTab === "legacy",
                "No one-time customers.",
              ),
              "single service",
              isOwner && sourceTab === "legacy" ? (
                <Button
                  size="sm"
                  variant="ghost"
                  className="ml-auto h-6 text-[11px]"
                  onClick={(e) => {
                    e.stopPropagation();
                    startDraft("one-time");
                  }}
                >
                  <Plus className="mr-1 h-3 w-3" />
                  Add one-time
                </Button>
              ) : undefined,
            )}
            {!source && sourceTab === "legacy" &&
              section(
                "Missed service",
                filteredMissed.length,
                openMissed,
                setOpenMissed,
                renderMissedTable(
                  filteredMissed,
                  "No missed services — every past job was completed or canceled.",
                ),
                "past jobs never completed",
              )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
